import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Share2, AlertCircle, ArrowRight, Clock, CheckCircle } from "lucide-react";
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';
import ReferralLinkGenerator from "@/components/affiliate/ReferralLinkGenerator";

export default function AffiliateDashboard() {
  const { session } = useAuthSession();
  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [applicationStatus, setApplicationStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [requestStatus, setRequestStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [canBeAffiliate, setCanBeAffiliate] = useState(false);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  
  useEffect(() => {
    const fetchAffiliateData = async () => {
      setLoading(true);
      setError(null);
      
      if (!session?.user?.id) {
        setLoading(false);
        return;
      }
      
      try {
        console.log('Fetching affiliate data for user:', session.user.id);
        
        // First check user_roles for can_be_affiliate flag
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('can_be_affiliate')
          .eq('user_id', session.user.id)
          .single();
          
        if (roleError && roleError.code !== 'PGRST116') {
          // Only throw if it's not the "no rows returned" error
          console.error('Error fetching user role data:', roleError);
          throw roleError;
        }
        
        if (roleData) {
          setCanBeAffiliate(roleData.can_be_affiliate || false);
        }
        
        // Check for affiliate request status
        const { data: requestData, error: requestError } = await supabase
          .from('affiliate_requests')
          .select('status')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .maybeSingle();
          
        if (requestError) {
          console.error('Error fetching request status:', requestError);
          throw requestError;
        }
        
        if (requestData) {
          setRequestStatus(requestData.status);
        }
        
        // Fetch affiliate status and referral code
        const { data: affiliateData, error: affiliateError } = await supabase
          .from('affiliates')
          .select('referral_code, status')
          .eq('user_id', session.user.id)
          .maybeSingle();
          
        if (affiliateError) {
          console.error('Error fetching affiliate data:', affiliateError);
          throw affiliateError;
        }
        
        if (affiliateData) {
          console.log('Found affiliate data:', affiliateData);
          setReferralCode(affiliateData.referral_code);
          setApplicationStatus(affiliateData.status);
          
          // Generate referral link
          const baseUrl = window.location.origin;
          setReferralLink(`${baseUrl}/refer/${affiliateData.referral_code}`);
          
          if (affiliateData.status === 'approved') {
            // Fetch affiliate stats using affiliate table's id
            const { data: affiliateIdData } = await supabase
              .from('affiliates')
              .select('id')
              .eq('user_id', session.user.id)
              .single();
              
            if (affiliateIdData) {
              // Fetch referral stats using the affiliate ID
              const { data: referralStats, error: referralStatsError } = await supabase
                .from('affiliate_referrals')
                .select('*')
                .eq('affiliate_id', affiliateIdData.id);
                
              if (referralStatsError) throw referralStatsError;
              
              console.log('Referral stats:', referralStats);
              
              // Set total referrals
              const total = referralStats ? referralStats.length : 0;
              setTotalReferrals(total);
              
              // For earnings, we'll use a fixed estimate since the subscription data might not be available
              // A more robust solution would be to store commission values in the affiliate_referrals table
              const estimatedEarningsPerReferral = 500; // ₹500 per referral as a placeholder
              setTotalEarnings(total * estimatedEarningsPerReferral);
            }
          }
        } else {
          // If not an affiliate, set status to null
          console.log('User is not an affiliate');
          setApplicationStatus(null);
        }
      } catch (error: any) {
        console.error('Error fetching affiliate data:', error);
        setError(`Failed to fetch affiliate data: ${error.message}`);
        toast.error(`Failed to fetch affiliate data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAffiliateData();
  }, [session?.user?.id]);
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out Edaptica!',
          text: 'Sign up using my referral link and get exclusive benefits!',
          url: referralLink,
        });
        console.log('Shared successfully');
      } catch (error) {
        console.error('Error sharing:', error);
        toast.error('Could not share the referral link.');
      }
    } else {
      toast.info('Web Share API not supported. Copy the link to share.');
    }
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopyButtonText('Copied!');
        toast.success('Referral link copied to clipboard!');
        setTimeout(() => {
          setCopyButtonText('Copy');
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy the referral link.');
      });
  };
  
  if (loading) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center space-y-2">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground">Loading affiliate dashboard...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!session) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p>Please sign in to view the affiliate dashboard.</p>
              <Button asChild className="mt-4">
                <Link to="/auth">Sign In</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Error Loading Affiliate Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 1: No request submitted yet
  if (requestStatus === null && applicationStatus === null) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Become an Affiliate</CardTitle>
            <CardDescription>
              Share Edaptica with others and earn commissions on subscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-6">Join our affiliate program to earn commission for every new customer you refer to Edaptica.</p>
            
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-100 flex items-start">
                <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                  <Clock className="h-4 w-4 text-blue-700" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Two-step Process</h3>
                  <p className="text-xs text-blue-700 mt-1">
                    First, submit a request to join. Once approved, you'll be able to complete your application with payment details.
                  </p>
                </div>
              </div>
              
              <Button asChild className="w-full">
                <Link to="/affiliate/request">
                  Request to Join <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 2: Request submitted, waiting for approval
  if (requestStatus === 'pending' && applicationStatus === null) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Request Pending</CardTitle>
            <CardDescription>
              Your request to join the affiliate program is being reviewed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start">
              <Clock className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
              <div>
                <p className="text-amber-800">Your request is currently under review by our team.</p>
                <p className="text-amber-700 text-sm mt-1">We'll notify you once your request has been approved.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 3: Request approved, can now apply
  if ((requestStatus === 'approved' || canBeAffiliate) && applicationStatus === null) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Request Approved!</CardTitle>
            <CardDescription>
              You can now complete your affiliate application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start mb-6">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
              <div>
                <p className="text-green-800">Your request to join our affiliate program has been approved.</p>
                <p className="text-green-700 text-sm mt-1">Please complete your application by providing your payment details.</p>
              </div>
            </div>
            
            <Button asChild className="w-full">
              <Link to="/affiliate/apply">
                Complete Application <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 4: Request rejected
  if (requestStatus === 'rejected' && applicationStatus === null) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Request Declined</CardTitle>
            <CardDescription>
              Your request to join the affiliate program was declined
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start mb-6">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
              <div>
                <p className="text-red-800">We're sorry, but your request to join our affiliate program was declined.</p>
                <p className="text-red-700 text-sm mt-1">If you believe this was a mistake, please contact our support team.</p>
              </div>
            </div>
            
            <Button asChild variant="outline" className="w-full">
              <Link to="/affiliate/request">
                Submit a New Request
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 5: Applied and waiting for approval
  if (applicationStatus === 'pending') {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Application Pending</CardTitle>
            <CardDescription>
              Your application is currently under review
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start">
              <Clock className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
              <div>
                <p className="text-amber-800">Your application is being reviewed by our team.</p>
                <p className="text-amber-700 text-sm mt-1">We will notify you once your application has been processed.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 6: Application rejected
  if (applicationStatus === 'rejected') {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Application Rejected</CardTitle>
            <CardDescription>
              Your affiliate application has been rejected
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start mb-6">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
              <div>
                <p className="text-red-800">Unfortunately, your affiliate application has been rejected.</p>
                <p className="text-red-700 text-sm mt-1">Please contact support for more information.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Step 7: Application approved - Use our new ReferralLinkGenerator component
  if (applicationStatus === 'approved') {
    return (
      <div className="container py-8">
        <ReferralLinkGenerator />
      </div>
    );
  }
  
  return (
    <div className="container max-w-xl py-8">
      <Card>
        <CardHeader>
          <CardTitle>Affiliate Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Something went wrong. Please try again later.</p>
          <Button onClick={() => window.location.reload()} className="mt-4">Reload</Button>
        </CardContent>
      </Card>
    </div>
  );
}
